import { Line2D, LineSegment2D, Polygon2D } from '@paragon/geometry-library';

import { SelectableItemOfType, SelectableItemType } from '../types';

const SELECTION_WINDOW_MODES = ['layout', 'webbing', 'design'] as const;
export type SelectionWindowMode = (typeof SELECTION_WINDOW_MODES)[number];

const SUPPORTED_TYPES = {
  layout: [
    'trussEnvelope',
    'beam',
    'wall',
    'roofPlane',
    'ceilingPlane',
    'ceilingTray',
    'workLine',
    'featureArea',
    'featurePatternContainer',
  ],
  design: ['member', 'platePair'],
  webbing: ['webSegment'],
} as const satisfies Record<SelectionWindowMode, SelectableItemType[]>;

export type WindowToolTargetType = (typeof SUPPORTED_TYPES)[SelectionWindowMode][number];

export function getRelevantWindowToolModeFor(type: string): SelectionWindowMode | null {
  return SELECTION_WINDOW_MODES.find((mode) => (SUPPORTED_TYPES[mode] as string[]).includes(type)) ?? null;
}

// null target shapes are allowed because this is just an intermediate type; it's easier to filter them all out later
//   rather than individually for each supported target
type TargetShape = Polygon2D | Line2D | LineSegment2D | null;

export type WindowToolTargetCollection = {
  [T in WindowToolTargetType]: (readonly [SelectableItemOfType<T>, TargetShape | TargetShape[]])[];
};

export type SupportedTargetTypeForMode<M extends SelectionWindowMode> = (typeof SUPPORTED_TYPES)[M][number];

type ModeTargetEntries<M extends SelectionWindowMode> = {
  [T in SupportedTargetTypeForMode<M>]: (readonly [SelectableItemOfType<T>, TargetShape | TargetShape[]])[];
};

const ALL_SUPPORTED_TARGET_TYPES = Object.values(SUPPORTED_TYPES).flat();
const EMPTY_TARGET_COLLECTION = Object.freeze(
  Object.fromEntries(ALL_SUPPORTED_TARGET_TYPES.map((type) => [type, []] as const)),
) as unknown as WindowToolTargetCollection;

/**
 * A type-safe way of creating a `WindowToolTargetCollection` and ensuring that all of the relevant object types for
 *   that mode have been provided. Used in the target selectors for multiselection.
 */
export function createTargetCollectionForMode<TMode extends SelectionWindowMode>(
  mode: TMode, // just for type validation
  targets: ModeTargetEntries<TMode>,
): WindowToolTargetCollection {
  return { ...EMPTY_TARGET_COLLECTION, ...targets };
}
